import { Injectable } from '@angular/core';
import { collection, doc, setDoc, getDocs, query, where, updateDoc, deleteDoc, CollectionReference, DocumentData, getDoc } from 'firebase/firestore';
import { FirebaseService } from './firebase.service';
import { UserService } from './user.service';
import { formatLocation } from 'app/utils/flight/flight-data-formatters';
import { normalizeBooleanFlag } from 'app/utils/boolean-normalizer';

export interface PersistentWatcher {
  id?: string;
  status?: 'active' | 'paused';
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class PersistentWatcherService {
  private collectionRef: CollectionReference<DocumentData>;

  constructor(private firebaseService: FirebaseService, private userService: UserService) {
    this.collectionRef = collection(this.firebaseService.firestore, 'persistent-flight-watchers');
  }

  private async getUserProfile() {
    const user = await this.userService.getUserProfile();
    if (!user) throw new Error('User not authenticated.');
    return user;
  }

  async createWatcher(data: any): Promise<{ saved: boolean; id?: string; reason?: string }> {
    const userProfile = await this.getUserProfile();
    const sanitizedInput = { ...data };
    delete sanitizedInput._tempId;
    delete sanitizedInput.isPreview;

    const normalizedFlags = this.normalizeWatcherFlags(sanitizedInput);
    const seatsNeeded = Math.max(1, sanitizedInput.seatsNeeded ?? 1);
    const fingerprint = await this.generateFingerprint({
      ...sanitizedInput,
      ...normalizedFlags,
      seatsNeeded,
    });

    const watchersRef = collection(this.firebaseService.firestore, 'persistent-flight-watchers');
    const q = query(watchersRef, where('userId', '==', userProfile.id), where('fingerprint', '==', fingerprint));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      return { saved: false, reason: 'Watcher already exists' };
    }

    const fromRaw = data.departureLocationName;
    let toRaw: string;
    if (data.arrivalLocationName) {
      toRaw = data.arrivalLocationName;
    } else {
      const generalType = data.arrivalGeneralType || 'Location';
      const parts = [] as string[];
      if (data.arrivalGeneralState) {
        parts.push(data.arrivalGeneralState);
      }
      if (data.arrivalGeneralCountry) {
        parts.push(data.arrivalGeneralCountry);
      }
      const locationSuffix = parts.length > 0 ? ` in ${parts.join(', ')}` : '';
      toRaw = `Any ${generalType}${locationSuffix}`;
    }
    const displayName = `${formatLocation(fromRaw)} to ${formatLocation(toRaw)}`;

    const firestoreDoc = {
      userId: userProfile.id,
      fingerprint,
      status: 'active',
      createdAt: new Date(),
      updatedAt: new Date(),
      ...sanitizedInput,
      checkedAt: null,
      displayName,
      seatsNeeded,
      ...normalizedFlags,
    };
    const docRef = doc(this.collectionRef);
    await setDoc(docRef, firestoreDoc);
    return { saved: true, id: docRef.id };
  }

  async getWatchersByUserId(userId: string): Promise<PersistentWatcher[]> {
    const q = query(this.collectionRef, where('userId', '==', userId));
    const snapshot = await getDocs(q);
    return Promise.all(snapshot.docs.map(doc => {
      const data = doc.data();
      const normalizedFlags = this.normalizeWatcherFlags(data);
      return this.withDiscoveries({ id: doc.id, ...data, ...normalizedFlags } as PersistentWatcher);
    }));
  }

  async getFlightWatcher(watcherId: string): Promise<PersistentWatcher | null> {
    const docRef = doc(this.collectionRef, watcherId);
    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) {
      return null;
    }

    const data = snapshot.data();
    const normalizedFlags = this.normalizeWatcherFlags(data);
    return this.withDiscoveries({ id: watcherId, ...data, ...normalizedFlags } as PersistentWatcher);
  }

  async updateWatcher(id: string, updates: Partial<PersistentWatcher>): Promise<void> {
    const ref = doc(this.collectionRef, id);
    await updateDoc(ref, updates);
  }

  async deleteWatcher(id: string): Promise<void> {
    const ref = doc(this.collectionRef, id);
    await deleteDoc(ref);
  }

  private async generateFingerprint(data: any): Promise<string> {
    const normalizedFlags = this.normalizeWatcherFlags(data);
    const relevant = {
      departureLocationName: data?.departureLocationName,
      arrivalLocationName: data?.arrivalLocationName,
      arrivalGeneralType: data?.arrivalGeneralType,
      arrivalGeneralState: data?.arrivalGeneralState,
      arrivalGeneralCountry: data?.arrivalGeneralCountry,
      seatsNeeded: Math.max(1, data?.seatsNeeded ?? 1),
      seatsOffered: data?.seatsOffered,
      ...normalizedFlags,
    };
    const jsonString = JSON.stringify(relevant);
    const encoder = new TextEncoder();
    const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(jsonString));
    return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
  }

  private normalizeWatcherFlags(data: any) {
    const petOk = normalizeBooleanFlag(data?.isPetOk);
    const childrenOk = normalizeBooleanFlag(data?.isChildrenOk);

    return {
      isPetOk: petOk ?? true,
      isChildrenOk: childrenOk ?? true,
    };
  }

  private async withDiscoveries(watcher: PersistentWatcher): Promise<PersistentWatcher> {
    if (!watcher.id) {
      return watcher;
    }
    const discoveriesRef = collection(this.firebaseService.firestore, 'persistent-flight-watchers', watcher.id, 'discoveries');
    const snapshot = await getDocs(discoveriesRef);
    return {
      ...watcher,
      discoveredFlights: snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: data['flightId'] || doc.id,
          discoveredAt: data['discoveredAt']
        };
      })
    };
  }
}
